/**
 * Leads: every opportunity in one place, replacing HubSpot.
 * - Inbound: website enquiries and fit checks arrive here (lib/lead-intake.ts) with a reply deadline
 *   (2 working days, as the website promises) and a drafted reply.
 * - Outbound: researched prospects, imported from a target list or added by hand, with their
 *   contacts and the four-touch sequence (lib/crm.ts).
 * - Partner introductions, linked to the partner who made them.
 * Every lead moves through the stages to a discovery call and an engagement letter, and becomes a
 * client and an audit in one step.
 */
import type { Db } from "../data/db.ts";
import { celebrate } from "./katana.ts";
import { staleLeads } from "../lib/insights.ts";
import { toast } from "./toast.ts";
import {
  CONCERNS,
  LEAD_STAGES,
  ORDER_BANDS,
  ORIGIN_LABEL,
  REPLY_WORKING_DAYS,
  SEQUENCE_LENGTH,
  STAGE_LABEL,
  draftReply,
  isOpen,
  leadNextAction,
  mailtoLink,
  requestLabel,
  triage,
  type LeadInput,
  type LeadNote,
  type LeadOrigin,
  type LeadRow,
  type LegalForm,
  type LeadStatus,
  type OrderBand,
} from "../lib/leads.ts";
import {
  CHANNEL_LABEL,
  OUTCOME_LABEL,
  SEQUENCE,
  domainOf,
  draftTouch,
  coldEmailRule,
  CALL_QUESTIONS,
  LEGAL_FORM_LABEL,
  duplicateOf,
  leadsCsv,
  nextTouchNo,
  parseTargetList,
  type ImportSource,
  pipelineValue,
  touchPatch,
  touchesLeftPatch,
  type Contact,
  type ImportProspect,
  type Touch,
  type TouchChannel,
  type TouchOutcome,
} from "../lib/crm.ts";
import { londonDate, replyDue } from "../lib/working-days.ts";
import { docsApi } from "../lib/documents-client.ts";
import { DOC_KIND_LABEL, DOC_STATUS_LABEL, FINAL_STATUSES, FREE_PLAN_DOCS_PER_YEAR, type DocKind, type LeadDocument } from "../lib/pandadoc.ts";
import { action, dropZone, date, deleteButton, download, gbp, h, mount, notice, stat, table } from "./dom.ts";
import { channelLabel, channelOf, defaultChannel, leadScore, lossReasonOf, LOSS_REASONS, NOT_RECORDED, parseTags, SOURCE_CHANNELS } from "../lib/sources.ts";
import { hasDraft, keepDraft } from "./drafts.ts";

const SOURCE_LABEL: Record<LeadRow["source"], string> = { "fit-check": "Fit check", enquiry: "Enquiry", manual: "Added by hand", import: "Target list" };
const PRIORITY_LABEL = { high: "High", normal: "Normal", low: "Low" } as const;
const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

/** "Today, 5.30pm", "Tomorrow, 5.30pm", "Tue 13 Oct, 5.30pm", or a date for all-day items. */
export function when(iso: string | null, now = new Date()): string {
  if (!iso) return "–";
  const allDay = iso.length === 10;
  const d = new Date(allDay ? `${iso}T12:00:00Z` : iso);
  const day = (x: Date) => x.toLocaleDateString("en-CA", { timeZone: "Europe/London" });
  const tomorrow = new Date(now.getTime() + 864e5);
  const label = day(d) === day(now) ? "Today" : day(d) === day(tomorrow) ? "Tomorrow"
    : d.toLocaleDateString("en-GB", { weekday: "short", day: "numeric", month: "short", timeZone: "Europe/London" });
  if (allDay) return label;
  const time = d.toLocaleTimeString("en-GB", { hour: "numeric", minute: "2-digit", hour12: true, timeZone: "Europe/London" }).replace(":00", "").replace(" ", "");
  return `${label}, ${time}`;
}

export function stagePill(s: LeadStatus): HTMLElement {
  return h("span", { class: `pill pill-lead-${s}` }, STAGE_LABEL[s]);
}
export function priorityPill(p: "high" | "normal" | "low"): HTMLElement {
  return h("span", { class: `pill pill-pri-${p}` }, PRIORITY_LABEL[p]);
}
export function originPill(o: LeadOrigin): HTMLElement {
  return h("span", { class: `pill pill-origin-${o}` }, o === "partner" ? "Partner" : ORIGIN_LABEL[o]);
}
export const leadTitle = (l: LeadRow) => (l.company && l.company !== l.name ? `${l.name}, ${l.company}` : l.name);

/** The next action, as a cell: red when late. */
export function nextCell(l: LeadRow, now = new Date()): HTMLElement | string {
  const n = leadNextAction(l, now);
  if (!n) return l.closed_reason ? h("span", { class: "muted" }, l.closed_reason) : "–";
  return h("span", { class: n.overdue ? "late" : undefined }, n.what, n.due ? h("span", { class: "muted small" }, ` · ${n.overdue ? "was due " : ""}${when(n.due, now)}`) : "");
}

const toLocalInput = (iso: string | null) => {
  if (!iso) return "";
  const d = new Date(iso);
  const p = (x: number) => String(x).padStart(2, "0");
  return `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())}T${p(d.getHours())}:${p(d.getMinutes())}`;
};

/** First name of whoever is signed in (yusuf@ → Yusuf), for signing drafts. */
const signer = (email: string | undefined) => {
  const first = email?.split("@")[0]!.split(/[._-]/)[0] ?? "";
  return first && first !== "demo" && first !== "business" ? `${first[0]!.toUpperCase()}${first.slice(1)}` : undefined;
};


const blankInput = (source: LeadInput["source"], name: string, extra: Partial<LeadInput> = {}): LeadInput => ({
  source, netlify_id: null, received_at: new Date().toISOString(), name, email: null, company: null, orders_band: null, request: null,
  how_you_ship: null, carriers: [], concerns: [], timing: null, verdict: null, message: null, source_page: null, reply_due: null, ...extra,
});

// ------------------------------------------------------------------------------- list

export async function leadsView(db: Db): Promise<HTMLElement> {
  const root = h("section", { class: "page" });
  const [leads, partners, me] = await Promise.all([db.listLeads(), db.listPartners(), db.currentUser()]);
  const now = new Date();
  const open = leads.filter((l) => isOpen(l.status));
  const lateReplies = open.filter((l) => l.status === "new" && leadNextAction(l, now)?.overdue).length;
  const touchesDue = open.filter((l) => (l.status === "prospect" || l.status === "contacted") && leadNextAction(l, now)?.overdue).length;
  const partnerName = new Map(partners.map((p) => [p.id!, p.name]));

  // ---- Filters (remembered for this tab)
  const saved = (() => { try { return JSON.parse(sessionStorage.getItem("leads-filter") ?? "{}") as Record<string, string>; } catch { return {}; } })();
  const show = h("select", { "aria-label": "Show" },
    h("option", { value: "open" }, `Open (${open.length})`), h("option", { value: "closed" }, `Closed (${leads.length - open.length})`), h("option", { value: "all" }, `All (${leads.length})`));
  const origin = h("select", { "aria-label": "Origin" }, h("option", { value: "" }, "All origins"),
    (Object.keys(ORIGIN_LABEL) as LeadOrigin[]).map((o) => h("option", { value: o }, ORIGIN_LABEL[o])));
  const stage = h("select", { "aria-label": "Stage" }, h("option", { value: "" }, "All stages"), LEAD_STAGES.map((s) => h("option", { value: s.status }, s.label)));
  const owner = h("select", { "aria-label": "Owner" }, h("option", { value: "" }, "Anyone"), h("option", { value: "me" }, "Mine"), h("option", { value: "none" }, "No owner"));
  const search = h("input", { type: "search", "aria-label": "Search", placeholder: "Search name, company, email, sector" });
  const view = h("select", { "aria-label": "View" }, h("option", { value: "list" }, "List"), h("option", { value: "board" }, "Board"));
  const channel = h("select", { "aria-label": "Source" }, h("option", { value: "" }, "All sources"),
    SOURCE_CHANNELS.map((c) => h("option", { value: c.key }, c.label)), h("option", { value: NOT_RECORDED }, "Not recorded"));
  const sort = h("select", { "aria-label": "Sort" }, h("option", { value: "due" }, "Sort: next due"), h("option", { value: "score" }, "Sort: score"), h("option", { value: "newest" }, "Sort: newest"), h("option", { value: "name" }, "Sort: A to Z"));
  show.value = saved.show ?? "open"; origin.value = saved.origin ?? ""; stage.value = saved.stage ?? ""; owner.value = saved.owner ?? ""; view.value = saved.view ?? "list";
  channel.value = saved.channel ?? ""; sort.value = saved.sort ?? "due";

  // ---- Saved views: name a set of filters and come back to it (kept in this browser)
  const VIEWS_KEY = "ryoma.leadViews";
  type SavedView = { name: string; f: Record<string, string> };
  const readViews = (): SavedView[] => { try { return JSON.parse(localStorage.getItem(VIEWS_KEY) ?? "[]") as SavedView[]; } catch { return []; } };
  const filterState = () => ({ show: show.value, origin: origin.value, stage: stage.value, owner: owner.value, view: view.value, channel: channel.value, sort: sort.value, q: search.value });
  const views = h("select", { "aria-label": "Saved filters" });
  const paintViews = () => mount(views, h("option", { value: "" }, "Saved filters"), readViews().map((v, i) => h("option", { value: String(i) }, v.name)));
  paintViews();
  views.addEventListener("change", () => {
    const v = readViews()[Number(views.value)];
    if (!v) return;
    show.value = v.f.show ?? "open"; origin.value = v.f.origin ?? ""; stage.value = v.f.stage ?? ""; owner.value = v.f.owner ?? ""; view.value = v.f.view ?? "list";
    channel.value = v.f.channel ?? ""; sort.value = v.f.sort ?? "due"; search.value = v.f.q ?? "";
    draw();
  });
  const saveView = h("button", { type: "button", class: "btn btn-quiet", onClick: () => {
    const name = prompt("Name this view (for example: Hot outbound, LinkedIn this month)")?.trim();
    if (!name) return;
    const list = readViews().filter((v) => v.name !== name);
    list.push({ name: name.slice(0, 60), f: filterState() });
    try { localStorage.setItem(VIEWS_KEY, JSON.stringify(list.slice(-20))); } catch { /* storage blocked */ }
    paintViews();
    views.value = String(list.length - 1);
  } }, "Save filters");
  const dropView = h("button", { type: "button", class: "btn btn-quiet", onClick: () => {
    const i = Number(views.value);
    const list = readViews();
    if (!views.value || !list[i] || !confirm(`Delete the view "${list[i]!.name}"?`)) return;
    list.splice(i, 1);
    try { localStorage.setItem(VIEWS_KEY, JSON.stringify(list)); } catch { /* storage blocked */ }
    paintViews();
  } }, "Delete saved");

  const contacts = await db.listContacts().catch(() => [] as Contact[]);
  const haystack = new Map(leads.map((l) => [l.id, [l.name, l.company, l.email, l.website, l.sector, l.source_detail, channelLabel(channelOf(l)), ...(l.tags ?? []), ...contacts.filter((c) => c.lead_id === l.id).flatMap((c) => [c.name, c.email, c.role])].filter(Boolean).join(" ").toLowerCase()]));

  const list = h("div");

  // ---- Bulk actions on the ticked leads: assign, move stage, tag, export
  const selected = new Set<string>();
  const owners = [...new Set([me?.email, ...leads.map((l) => l.owner)].filter((x): x is string => !!x && x.endsWith("@ryoma-insight.co.uk")))];
  const bulkCount = h("strong", null, "0");
  const bulkOwner = h("select", { "aria-label": "Assign to" }, h("option", { value: "" }, "Assign to…"), owners.map((o) => h("option", { value: o }, o.split("@")[0]!)), h("option", { value: "none" }, "No owner"));
  const bulkStage = h("select", { "aria-label": "Move to stage" }, h("option", { value: "" }, "Move to stage…"),
    LEAD_STAGES.filter((st) => st.open).map((st) => h("option", { value: st.status }, st.label)));
  const bulkTag = h("input", { type: "text", "aria-label": "Add tags", placeholder: "Add tags", maxlength: 120 });
  const chosen = () => leads.filter((l) => selected.has(l.id));
  const bulkApply = action("Apply", async (_b, st) => {
    const tags = parseTags(bulkTag.value);
    if (!bulkOwner.value && !bulkStage.value && !tags.length) throw new Error("Choose an owner, a stage or a tag first");
    let n = 0;
    for (const l of chosen()) {
      const patch: Parameters<Db["updateLead"]>[1] = {};
      if (bulkOwner.value) patch.owner = bulkOwner.value === "none" ? null : bulkOwner.value;
      if (bulkStage.value && bulkStage.value !== l.status && !l.do_not_contact) patch.status = bulkStage.value as LeadStatus;
      if (tags.length) patch.tags = parseTags([...(l.tags ?? []), ...tags].join(","));
      if (!Object.keys(patch).length) continue;
      await db.updateLead(l.id, patch);
      if (patch.status) await db.addLeadNote({ lead_id: l.id, kind: "status", body: `${STAGE_LABEL[l.status]} → ${STAGE_LABEL[patch.status]} (changed with others)` });
      n++;
      st.textContent = `${n} of ${selected.size}…`;
    }
    toast(`${n} lead${n === 1 ? "" : "s"} updated`);
    window.dispatchEvent(new HashChangeEvent("hashchange"));
  }, { primary: true });
  const bulkExport = action("Export selected", async () => {
    download(`ryoma-leads-selected-${londonDate(new Date())}.csv`, leadsCsv(chosen(), contacts, partners));
  });
  const bulkClear = h("button", { type: "button", class: "btn btn-quiet", onClick: () => { selected.clear(); draw(); } }, "Clear");
  const bulkBar = h("div", { class: "bulk-bar", role: "region", "aria-label": "Change the ticked leads", hidden: true },
    h("span", null, bulkCount, " ticked"), bulkOwner, bulkStage, bulkTag, bulkApply, bulkExport, bulkClear);
  const paintBulk = () => { bulkBar.hidden = selected.size === 0; bulkCount.textContent = String(selected.size); };

  const filtered = () => {
    const q = search.value.trim().toLowerCase();
    return leads.filter((l) =>
      (show.value === "open" ? isOpen(l.status) : show.value === "closed" ? !isOpen(l.status) : true) &&
      (!origin.value || l.origin === origin.value) &&
      (!stage.value || l.status === stage.value) &&
      (!owner.value || (owner.value === "me" ? l.owner === me?.email : !l.owner)) &&
      (!channel.value || channelOf(l) === channel.value) &&
      (!q || q.split(/\s+/).every((w) => haystack.get(l.id)!.includes(w))));
  };
  const draw = () => {
    try { sessionStorage.setItem("leads-filter", JSON.stringify(filterState())); } catch { /* private mode */ }
    const shown = filtered();
    if (sort.value === "score") shown.sort((a, b) => leadScore(b, now) - leadScore(a, now));
    else if (sort.value === "newest") shown.sort((a, b) => (b.received_at || b.created_at).localeCompare(a.received_at || a.created_at));
    else if (sort.value === "name") shown.sort((a, b) => leadTitle(a).localeCompare(leadTitle(b)));
    else if (show.value !== "closed") {
      shown.sort((a, b) => {
        const na = leadNextAction(a, now), nb = leadNextAction(b, now);
        return Number(nb?.overdue ?? false) - Number(na?.overdue ?? false) || (na?.due ?? "9").localeCompare(nb?.due ?? "9");
      });
    }
    for (const id of [...selected]) if (!shown.some((l) => l.id === id)) selected.delete(id);
    paintBulk();
    if (!shown.length) {
      mount(list, notice("info", leads.length ? "Nothing matches these filters." : "No leads yet. Website enquiries and fit checks appear here as soon as they are sent; add prospects below or import a target list."));
      return;
    }
    if (view.value === "board") {
      const stages = LEAD_STAGES.filter((s) => (show.value === "closed" ? !s.open : show.value === "open" ? s.open : true));
      mount(list, h("div", { class: "board" }, stages.map((s) => {
        const inStage = shown.filter((l) => l.status === s.status);
        return h("div", { class: "board-col" },
          h("h3", null, s.label, h("span", { class: "muted small" }, ` ${inStage.length}`)),
          inStage.map((l) => {
            const t = triage(l);
            return h("a", { class: "board-card", href: `#/lead/${l.id}` },
              h("strong", null, l.company ?? l.name), l.company && l.company !== l.name ? h("span", { class: "muted small" }, l.name) : "",
              h("span", { class: "small" }, t.offer), h("span", { class: "small" }, nextCell(l, now)));
          }));
      })));
      return;
    }
    const quietDays = new Map(staleLeads(shown, now).map((q) => [q.lead.id, q.quietDays]));
    const all = h("input", { type: "checkbox", "aria-label": "Select all leads in the list", checked: shown.length > 0 && shown.every((l) => selected.has(l.id)) });
    all.addEventListener("change", () => { for (const l of shown) { if (all.checked) selected.add(l.id); else selected.delete(l.id); } draw(); });
    mount(list, h("div", { class: "select-all" }, h("label", null, all, ` Select all ${shown.length}`)), table(["", "Who", "Source", "Orders a month", "Offer", "Score", "Stage", "Owner", "Next"], shown.map((l) => {
      const t = triage(l);
      const quiet = quietDays.get(l.id);
      const box = h("input", { type: "checkbox", "aria-label": `Select ${leadTitle(l)}`, checked: selected.has(l.id) });
      box.addEventListener("change", () => { if (box.checked) selected.add(l.id); else selected.delete(l.id); paintBulk(); });
      const score = leadScore(l, now);
      return [
        box,
        h("span", { class: "who-cell" }, h("a", { href: `#/lead/${l.id}` }, leadTitle(l)), l.tags?.length ? h("span", { class: "tags" }, l.tags.slice(0, 3).map((tg) => h("span", { class: "tag" }, tg))) : ""),
        h("span", { class: "source-cell" }, originPill(l.origin), h("span", { class: "small" }, channelOf(l) === NOT_RECORDED ? h("span", { class: "muted" }, "Source not recorded") : channelLabel(channelOf(l)).replace(/ \(.*\)$/, "")),
          l.source_detail ? h("span", { class: "muted small" }, l.source_detail) : l.partner_id ? h("span", { class: "muted small" }, partnerName.get(l.partner_id) ?? "") : ""),
        l.orders_band ?? "–",
        t.offer,
        h("span", { class: "score-cell" }, isOpen(l.status) ? h("span", { class: `score score-${score >= 60 ? "hot" : score >= 35 ? "warm" : "cool"}`, title: "Lead score: fit, keenness and engagement" }, String(score)) : "", priorityPill(t.priority)),
        h("span", { class: "stage-cell" }, stagePill(l.status), quiet ? h("span", { class: "pill pill-quiet", title: `No contact for ${quiet} days` }, `Quiet ${quiet}d`) : ""),
        l.owner ? l.owner.split("@")[0]! : h("span", { class: "muted" }, "–"),
        nextCell(l, now),
      ];
    })));
  };
  for (const el of [show, origin, stage, owner, view, channel, sort]) el.addEventListener("change", draw);
  search.addEventListener("input", draw);
  draw();

  // ---- Add a lead or prospect by hand
  const how = h("select", { "aria-label": "How we met them" },
    h("option", { value: "outbound" }, "We are approaching them (outbound prospect)"),
    h("option", { value: "inbound" }, "They contacted us (call, email, in person)"),
    h("option", { value: "partner" }, "A partner introduced them"));
  const company = h("input", { type: "text", "aria-label": "Company", maxlength: 200 });
  const website = h("input", { type: "text", "aria-label": "Website", maxlength: 200, placeholder: "example.co.uk" });
  const orders = h("select", { "aria-label": "Orders a month" }, h("option", { value: "" }, "Not known"), ORDER_BANDS.map((b) => h("option", { value: b }, b)));
  const sector = h("input", { type: "text", "aria-label": "Sector", maxlength: 100, placeholder: "Pet supplies, supplements…" });
  const name = h("input", { type: "text", "aria-label": "Contact name", maxlength: 200 });
  const role = h("input", { type: "text", "aria-label": "Role", maxlength: 120 });
  const email = h("input", { type: "text", "aria-label": "Email", maxlength: 254 });
  const linkedin = h("input", { type: "text", "aria-label": "LinkedIn", maxlength: 300 });
  const partner = h("select", { "aria-label": "Partner" }, h("option", { value: "" }, partners.length ? "Choose the partner" : "Add the partner on the Partners page first"),
    partners.map((p) => h("option", { value: p.id! }, p.name)));
  const message = h("textarea", { "aria-label": "Notes", maxlength: 4000, rows: 2 });
  // Where they came from: a channel from a fixed list (so Insights can compare channels) and the detail.
  const source = h("select", { "aria-label": "Source" }, SOURCE_CHANNELS.filter((c) => c.key !== "website").map((c) => h("option", { value: c.key }, c.label)));
  const sourceDetail = h("input", { type: "text", "aria-label": "Source detail", maxlength: 200, placeholder: "Campaign, event, group or who referred them" });
  const tagsIn = h("input", { type: "text", "aria-label": "Tags", maxlength: 300, placeholder: "Shopify, fashion, trade show (commas between)" });
  let sourceTouched = false;
  source.addEventListener("change", () => { sourceTouched = true; });
  source.value = defaultChannel("outbound");
  const add = action("Add", async () => {
    const o = how.value as LeadOrigin;
    const c = company.value.trim(), n = name.value.trim();
    if (!c && !n) throw new Error("Enter a company or a name");
    const e = email.value.trim().toLowerCase();
    if (e && !EMAIL_RE.test(e)) throw new Error("That email does not look right");
    if (o === "partner" && !partner.value) throw new Error("Choose the partner who introduced them");
    const dup = duplicateOf({ company: c || n, website: website.value.trim() || null, contacts: e ? [{ email: e } as Contact] : [] }, leads, contacts);
    if (dup && !confirm(`${dup.lead.company ?? dup.lead.name}: ${dup.reason}. Add anyway?`)) return;
    if (dup?.lead.do_not_contact) throw new Error("They asked not to be contacted. Do not add them again.");
    const received = new Date();
    const lead = await db.createLead(
      blankInput("manual", n || c, {
        received_at: received.toISOString(), email: e || null, company: c || null, orders_band: (orders.value || null) as OrderBand | null,
        message: message.value.trim() || null,
        reply_due: o === "outbound" ? null : replyDue(received, REPLY_WORKING_DAYS).toISOString(),
      }),
      {
        origin: o, website: website.value.trim() || null, sector: sector.value.trim() || null, partner_id: o === "partner" ? partner.value : null,
        status: o === "outbound" ? "prospect" : "new", owner: me?.email.endsWith("@ryoma-insight.co.uk") ? me.email : null,
        next_touch_due: o === "outbound" ? londonDate(received) : null,
        source_channel: source.value, source_detail: sourceDetail.value.trim() || null, tags: parseTags(tagsIn.value),
      });
    if (n) await db.saveContact({ lead_id: lead.id, name: n, role: role.value.trim() || null, email: e || null, linkedin: linkedin.value.trim() || null, phone: null, is_primary: true, do_not_contact: false });
    addDraft.clear();
    location.hash = `#/lead/${lead.id}`;
  }, { primary: true });
  const partnerRow = [h("label", null, "Partner"), partner];
  const syncHow = () => {
    partnerRow.forEach((el) => el.toggleAttribute("hidden", how.value !== "partner"));
    if (!sourceTouched) source.value = defaultChannel(how.value as LeadOrigin);
  };
  how.addEventListener("change", syncHow);
  syncHow();
  // Keep what is typed as a draft, so leaving the page or the tab loses nothing.
  const addPanel = h("details", { class: "card", open: hasDraft("add-lead") });
  const addDraft = keepDraft("add-lead", { how, company, website, orders, sector, name, role, email, linkedin, partner, message, source, sourceDetail, tags: tagsIn }, () => { addPanel.open = true; sourceTouched = true; });

  // ---- Import a target list
  const file = h("input", { type: "file", accept: ".csv,text/csv", "aria-label": "Target list CSV" });
  const preview = h("div");
  let ready: ImportProspect[] = [];
  let readySource: ImportSource | null = null;
  file.addEventListener("change", async () => {
    const f = file.files?.[0];
    if (!f) return;
    const { prospects, problems, source } = parseTargetList(await f.text());
    readySource = source;
    const rows = prospects.map((p) => ({ p, dup: duplicateOf(p, leads, contacts) }));
    ready = rows.filter((r) => !r.dup).map((r) => r.p);
    mount(preview,
      problems.length ? notice("warn", h("ul", { class: "checks" }, problems.slice(0, 20).map((x) => h("li", null, x)))) : "",
      rows.length ? table(["Company", "Website", "Orders a month", "Contacts", "Import?"], rows.map(({ p, dup }) => [
        p.company, p.website ?? "–", p.orders_band ?? "–", p.contacts.map((c) => c.name).join(", ") || "–",
        dup ? h("span", { class: dup.lead.do_not_contact ? "late" : "muted" }, `Skipped: ${dup.reason}`) : "Yes",
      ])) : "",
      ready.length ? h("p", null, importBtn) : notice("info", "Nothing new to import."),
    );
  });
  const importBtn = action("Import these prospects", async (_b, st) => {
    let n = 0;
    const today = londonDate(new Date());
    for (const p of ready) {
      const primary = p.contacts[0];
      const lead = await db.createLead(
        blankInput("import", primary?.name ?? p.company, { company: p.company, email: primary?.email ?? null, orders_band: p.orders_band, message: p.notes }),
        { origin: "outbound", website: p.website, sector: p.sector, status: "prospect", next_touch_due: today, owner: me?.email.endsWith("@ryoma-insight.co.uk") ? me.email : null,
          ...(readySource ? { source_channel: readySource.channel, source_detail: readySource.detail } : {}) });
      for (const c of p.contacts) await db.saveContact({ ...c, lead_id: lead.id });
      n++;
      st.textContent = `${n} of ${ready.length}…`;
    }
    st.textContent = `Imported ${n}.`;
    window.dispatchEvent(new HashChangeEvent("hashchange"));
  }, { primary: true });

  const exportBtn = action("Export CSV", async () => {
    download(`ryoma-leads-${londonDate(new Date())}.csv`, leadsCsv(leads, contacts, partners));
  });
  const value = pipelineValue(leads);

  mount(root,
    h("div", { class: "page-head" }, h("h1", null, "Leads"), h("span", { class: "due" }, `${open.length} open · ${leads.length} in all`)),
    h("div", { class: "filters", role: "search", "aria-label": "Filter leads" }, search, show, origin, channel, stage, owner, sort, view,
      h("span", { class: "filters-views" }, views, saveView, dropView), exportBtn),
    bulkBar,
    h("p", { class: "muted" }, `Website enquiries and fit checks arrive here as they are sent (reply within ${REPLY_WORKING_DAYS} working days). Outbound prospects run the ${SEQUENCE_LENGTH}-touch sequence. Pipeline and Partners show the numbers.`),
    h("div", { class: "stats" },
      stat("Replies due", String(open.filter((l) => l.status === "new").length), lateReplies ? `${lateReplies} late` : undefined, lateReplies ? "bad" : undefined),
      stat("Prospects in sequence", String(open.filter((l) => l.status === "prospect" || l.status === "contacted").length), touchesDue ? `${touchesDue} touches overdue` : undefined, touchesDue ? "warn" : undefined),
      stat("In conversation", String(open.filter((l) => ["replied", "call_booked", "call_done", "proposal_sent"].includes(l.status)).length)),
      stat("Open pipeline", gbp(value.pence), `audit fees from ${value.counted} lead${value.counted === 1 ? "" : "s"}${value.unknown ? `; ${value.unknown} with no order band` : ""}`),
      stat("Clients won", String(leads.filter((l) => l.status === "won").length)),
    ),
    list,
    (addPanel.append(
      h("summary", null, "Add a prospect or lead"),
      h("div", { class: "form-grid" },
        h("label", null, "How we met them"), how, ...partnerRow,
        h("label", null, "Source"), source, h("label", null, "Source detail"), sourceDetail,
        h("label", null, "Company"), company, h("label", null, "Website"), website,
        h("label", null, "Orders a month"), orders, h("label", null, "Sector"), sector,
        h("label", null, "Contact name"), name, h("label", null, "Role"), role,
        h("label", null, "Email"), email, h("label", null, "LinkedIn"), linkedin,
        h("label", null, "Tags"), tagsIn,
        h("label", null, "Notes"), message, h("span"), h("span", { class: "action" }, add, addDraft.status),
      ),
      h("p", { class: "muted small" }, "Business contact details from public sources only (company sites, LinkedIn), as our privacy notice says. Never buy lists. What you type is kept as a draft in this browser until you add the lead."),
    ), addPanel),
    h("details", { class: "card" },
      h("summary", null, "Import a target list (CSV)"),
      h("p", { class: "muted small" }, "One row per person. Headings: company, website, orders, sector, contact name, role, email, linkedin, notes. Rows with the same company or website become one prospect with several contacts. Anyone already on our books, or who asked not to be contacted, is skipped. The file is read in your browser."),
      dropZone(file), preview,
    ),
  );
  return root;
}

// ------------------------------------------------------------------------------- one lead

export async function leadView(db: Db, id: string): Promise<HTMLElement> {
  const root = h("section", { class: "page" });
  const render = async () => {
    const [lead, notes, audits, me, contacts, touches, partners, documents, allDocs] = await Promise.all([
      db.getLead(id), db.listLeadNotes(id), db.listAudits(), db.currentUser(), db.listContacts(id), db.listTouches(id), db.listPartners(),
      db.listDocuments(id).catch(() => [] as LeadDocument[]), db.listDocuments().catch(() => [] as LeadDocument[]),
    ]);
    if (!lead) {
      mount(root, notice("error", "Lead not found. It may have been deleted."), h("p", null, h("a", { href: "#/leads" }, "Back to leads")));
      return;
    }
    const now = new Date();
    const t = triage(lead);
    const next = leadNextAction(lead, now);
    const audit = lead.audit_id ? audits.find((a) => a.id === lead.audit_id) : undefined;
    const log = (kind: LeadNote["kind"], body: string) => db.addLeadNote({ lead_id: lead.id, kind, body });
    const partner = lead.partner_id ? partners.find((p) => p.id === lead.partner_id) : undefined;
    const fromWebsite = lead.source === "enquiry" || lead.source === "fit-check";
    const outbound = lead.origin === "outbound";
    const sign = signer(me?.email);

    // ---- Details
    const told: [string, string | HTMLElement][] = [
      ["From", `${ORIGIN_LABEL[lead.origin]} · ${SOURCE_LABEL[lead.source]}${lead.source_page ? ` (${lead.source_page} page)` : ""}, ${date(lead.received_at)}`],
    ];
    if (partner) told.push(["Introduced by", h("a", { href: "#/partners" }, partner.name)]);
    told.push(["Company", lead.company ?? "–"], ["Website", lead.website ?? (domainOf(lead.company) ?? "–")], ["Email", lead.email ?? "–"], ["Orders a month", lead.orders_band ?? "–"]);
    told.push(["Source", channelOf(lead) === NOT_RECORDED ? h("span", { class: "muted" }, "Not recorded: add it under Edit details") : `${channelLabel(channelOf(lead))}${lead.source_detail ? `: ${lead.source_detail}` : ""}`]);
    if (lead.tags?.length) told.push(["Tags", h("span", { class: "tags" }, lead.tags.map((tg) => h("span", { class: "tag" }, tg)))]);
    if (lead.sector) told.push(["Sector", lead.sector]);
    told.push(["Legal form", lead.legal_form ? LEGAL_FORM_LABEL[lead.legal_form] : h("span", { class: "muted" }, "Not recorded: check Companies House, then add it under Edit details")]);
    if (lead.request) told.push(["Asked for", requestLabel(lead.request)!]);
    if (lead.how_you_ship) told.push(["How they ship", lead.how_you_ship]);
    if (lead.carriers.length) told.push(["Carriers", lead.carriers.join(", ")]);
    if (lead.concerns.length) told.push(["What worries them", lead.concerns.map((c) => CONCERNS[c] ?? c).join("; ")]);
    if (lead.timing) told.push(["When", lead.timing]);
    if (lead.verdict) told.push(["The website told them", lead.verdict]);
    if (lead.message) told.push([fromWebsite ? "Message" : "Notes", lead.message]);

    const eCompany = h("input", { type: "text", value: lead.company ?? "", "aria-label": "Company", maxlength: 200 });
    const eWebsite = h("input", { type: "text", value: lead.website ?? "", "aria-label": "Website", maxlength: 200 });
    const eSector = h("input", { type: "text", value: lead.sector ?? "", "aria-label": "Sector", maxlength: 100 });
    const eOrders = h("select", { "aria-label": "Orders a month" }, h("option", { value: "" }, "Not known"), ORDER_BANDS.map((b) => h("option", { value: b, selected: b === lead.orders_band }, b)));
    const eOrigin = h("select", { "aria-label": "Origin" }, (Object.keys(ORIGIN_LABEL) as LeadOrigin[]).map((o) => h("option", { value: o, selected: o === lead.origin }, ORIGIN_LABEL[o])));
    const ePartner = h("select", { "aria-label": "Partner" }, h("option", { value: "" }, "None"), partners.map((p) => h("option", { value: p.id!, selected: p.id === lead.partner_id }, p.name)));
    const current = channelOf(lead);
    const eSource = h("select", { "aria-label": "Source" }, current === NOT_RECORDED ? h("option", { value: "" }, "Not recorded") : "",
      SOURCE_CHANNELS.map((c) => h("option", { value: c.key, selected: c.key === current }, c.label)));
    const eSourceDetail = h("input", { type: "text", value: lead.source_detail ?? "", "aria-label": "Source detail", maxlength: 200, placeholder: "Campaign, event, group or person" });
    const eTags = h("input", { type: "text", value: (lead.tags ?? []).join(", "), "aria-label": "Tags", maxlength: 300, placeholder: "Commas between tags" });
    const eLegal = h("select", { "aria-label": "Legal form" }, h("option", { value: "" }, "Not checked yet"),
      (Object.keys(LEGAL_FORM_LABEL) as LegalForm[]).map((k) => h("option", { value: k, selected: k === lead.legal_form }, LEGAL_FORM_LABEL[k])));
    const saveDetails = action("Save details", async (_b, st) => {
      if (eOrigin.value === "partner" && !ePartner.value) throw new Error("Choose the partner who introduced them");
      await db.updateLead(lead.id, {
        company: eCompany.value.trim() || null, website: eWebsite.value.trim() || null, sector: eSector.value.trim() || null,
        orders_band: (eOrders.value || null) as OrderBand | null, origin: eOrigin.value as LeadOrigin, partner_id: ePartner.value || null,
        source_channel: eSource.value || null, source_detail: eSourceDetail.value.trim() || null, tags: parseTags(eTags.value),
        legal_form: (eLegal.value || null) as LegalForm | null,
      });
      st.textContent = "Saved";
      await render();
    });

    // ---- Contacts
    const contactRows = contacts.map((c) => {
      const primary = action(c.is_primary ? "Primary" : "Make primary", async () => { await db.saveContact({ ...c, is_primary: true }); await render(); }, { disabled: c.is_primary });
      const remove = action("Remove", async () => {
        if (!confirm(`Remove ${c.name}?`)) return;
        await db.deleteContact(c.id!);
        await log("note", `Contact removed: ${c.name}.`);
        await render();
      });
      return [h("strong", null, c.name), c.role ?? "–", c.email ? h("a", { href: `mailto:${c.email}` }, c.email) : "–",
        c.linkedin ? h("a", { href: /^https?:\/\//.test(c.linkedin) ? c.linkedin : `https://${c.linkedin}`, target: "_blank", rel: "noopener" }, "Profile") : "–",
        c.do_not_contact ? h("span", { class: "late" }, "Do not contact") : primary, remove];
    });
    const cName = h("input", { type: "text", "aria-label": "Name", maxlength: 200, placeholder: "Name" });
    const cRole = h("input", { type: "text", "aria-label": "Role", maxlength: 120, placeholder: "Role" });
    const cEmail = h("input", { type: "text", "aria-label": "Email", maxlength: 254, placeholder: "Email" });
    const cLinked = h("input", { type: "text", "aria-label": "LinkedIn", maxlength: 300, placeholder: "LinkedIn URL" });
    const addContact = action("Add contact", async () => {
      if (!cName.value.trim()) throw new Error("Enter a name");
      const e = cEmail.value.trim().toLowerCase();
      if (e && !EMAIL_RE.test(e)) throw new Error("That email does not look right");
      await db.saveContact({ lead_id: lead.id, name: cName.value.trim(), role: cRole.value.trim() || null, email: e || null, linkedin: cLinked.value.trim() || null, phone: null, is_primary: contacts.length === 0, do_not_contact: false });
      await render();
    });

    // ---- Sequence: log a touch, with a draft for the next one
    const touchNo = nextTouchNo(lead);
    const step = SEQUENCE.find((s) => s.no === touchNo);
    const tContact = h("select", { "aria-label": "Contact" }, h("option", { value: "" }, "Company (no one person)"),
      contacts.filter((c) => !c.do_not_contact).map((c) => h("option", { value: c.id!, selected: c.is_primary }, c.name)));
    const tChannel = h("select", { "aria-label": "Channel" }, (Object.keys(CHANNEL_LABEL) as TouchChannel[]).map((c) => h("option", { value: c, selected: c === (step?.channel ?? "email") }, CHANNEL_LABEL[c])));
    const tOutcome = h("select", { "aria-label": "Outcome" }, (Object.keys(OUTCOME_LABEL) as TouchOutcome[]).map((o) => h("option", { value: o }, OUTCOME_LABEL[o])));
    const tWhen = h("input", { type: "datetime-local", value: toLocalInput(now.toISOString()), "aria-label": "When" });
    const tNote = h("input", { type: "text", "aria-label": "Note", maxlength: 1000, placeholder: "What you sent or what they said" });
    const contactOf = (cid: string) => contacts.find((c) => c.id === cid);
    const draft = draftTouch(lead, contactOf(tContact.value)?.name ?? null, touchNo, sign);
    const dSubject = h("input", { type: "text", value: draft.subject, "aria-label": "Subject", class: "wide" });
    const dBody = h("textarea", { "aria-label": "Draft", rows: 10 }, draft.body);
    const dMail = h("a", { class: "btn", href: "#", target: "_blank", rel: "noopener" }, "Open in email");
    const syncDraft = () => {
      const to = contactOf(tContact.value)?.email ?? lead.email;
      dMail.setAttribute("href", to ? mailtoLink(to, { subject: dSubject.value, body: dBody.value }) : "#");
    };
    tContact.addEventListener("change", () => {
      const d = draftTouch(lead, contactOf(tContact.value)?.name ?? null, touchNo, sign);
      dSubject.value = d.subject;
      dBody.value = d.body;
      syncDraft();
    });
    dSubject.addEventListener("input", syncDraft);
    dBody.addEventListener("input", syncDraft);
    syncDraft();
    const touchDraft = keepDraft(`touch-${lead.id}-${touchNo}`, { subject: dSubject, body: dBody, note: tNote }, syncDraft);
    const logTouch = action(`Log touch ${touchNo}`, async () => {
      if (lead.do_not_contact) throw new Error("They asked not to be contacted");
      const outcome = tOutcome.value as TouchOutcome;
      if (tChannel.value === "email" && outcome === "sent" && !coldEmailRule(lead.legal_form).allowed) throw new Error(coldEmailRule(lead.legal_form).why!);
      if (outcome === "sent" && /\[[^\]]+\]/.test(dBody.value) && tChannel.value === "email" && !confirm("The draft still has [placeholders]. Log it as sent anyway?")) return;
      const at = tWhen.value ? new Date(tWhen.value).toISOString() : new Date().toISOString();
      const c = contactOf(tContact.value);
      await db.addTouch({ lead_id: lead.id, contact_id: c?.id ?? null, touch_no: touchNo, channel: tChannel.value as TouchChannel, outcome, at, note: tNote.value.trim() || null });
      const patch = touchPatch(lead, { touch_no: touchNo, outcome, at });
      await db.updateLead(lead.id, patch);
      await log("touch", `Touch ${touchNo} by ${CHANNEL_LABEL[tChannel.value as TouchChannel].toLowerCase()}${c ? ` to ${c.name}` : ""}: ${OUTCOME_LABEL[outcome].toLowerCase()}.${tNote.value.trim() ? ` ${tNote.value.trim()}` : ""}`);
      if (patch.status && patch.status !== lead.status) await log("status", `${STAGE_LABEL[lead.status]} → ${STAGE_LABEL[patch.status]}`);
      if (outcome === "opted_out") await optOut(false);
      touchDraft.clear();
      await render();
    }, { primary: true });

    // ---- Do not contact
    const optOut = async (ask = true) => {
      if (ask && !confirm(`Record that ${lead.company ?? lead.name} asked not to be contacted? We keep only their name, company and email so we never approach them again; other details and notes are removed.`)) return;
      for (const c of contacts) await db.saveContact({ ...c, linkedin: null, phone: null, role: null, do_not_contact: true });
      await db.updateLead(lead.id, { do_not_contact: true, status: "lost", closed_reason: "Asked not to be contacted", message: null, next_step: null, next_step_due: null, next_touch_due: null });
      await log("system", "Asked not to be contacted. Kept as a do-not-contact record only.");
    };
    const optOutBtn = action("They asked not to be contacted", async () => { await optOut(); await render(); });

    // ---- Reply (website enquiries and partner introductions)
    const rDraft = draftReply(lead, sign);
    const subject = h("input", { type: "text", value: rDraft.subject, "aria-label": "Subject", class: "wide" });
    const body = h("textarea", { "aria-label": "Reply", rows: 14, class: "mono-ish" }, rDraft.body);
    const openMail = h("a", { class: "btn", href: "#", target: "_blank", rel: "noopener" }, "Open in email");
    const syncMail = () => openMail.setAttribute("href", lead.email ? mailtoLink(lead.email, { subject: subject.value, body: body.value }) : "#");
    subject.addEventListener("input", syncMail);
    body.addEventListener("input", syncMail);
    syncMail();
    const replyDraft = keepDraft(`reply-${lead.id}`, { subject, body }, syncMail);
    const copy = action("Copy reply", async (_b, st) => {
      await navigator.clipboard.writeText(`Subject: ${subject.value}\n\n${body.value}`);
      st.textContent = "Copied";
    });
    const markReplied = action("Mark replied", async () => {
      if (/\[[^\]]+\]/.test(body.value) && !confirm("The reply still has [placeholders]. Mark it replied anyway?")) return;
      const at = new Date().toISOString();
      await db.updateLead(lead.id, { status: lead.status === "new" ? "replied" : lead.status, replied_at: at, last_contact_at: at });
      await log("email", `Replied: "${subject.value}"`);
      replyDraft.clear();
      await render();
    }, { primary: lead.status === "new" });

    // ---- Progress
    const stageSel = h("select", { "aria-label": "Stage" }, LEAD_STAGES.map((s) => h("option", { value: s.status, selected: s.status === lead.status }, s.label)));
    const owner = h("input", { type: "text", value: lead.owner ?? "", "aria-label": "Owner", placeholder: "name@ryoma-insight.co.uk", maxlength: 254 });
    const callAt = h("input", { type: "datetime-local", value: toLocalInput(lead.call_at), "aria-label": "Discovery call" });
    const nextStep = h("input", { type: "text", value: lead.next_step ?? "", "aria-label": "Next step", maxlength: 300 });
    const nextDue = h("input", { type: "date", value: lead.next_step_due ?? "", "aria-label": "Next step due" });
    const knownReason = lossReasonOf(lead.closed_reason);
    const reasonSel = h("select", { "aria-label": "Why it closed" }, h("option", { value: "" }, "Choose why…"), LOSS_REASONS.map((r) => h("option", { value: r, selected: r === knownReason }, r)));
    const reasonText = lead.closed_reason && knownReason && lead.closed_reason.startsWith(`${knownReason}: `) ? lead.closed_reason.slice(knownReason.length + 2) : knownReason === "Other" && lead.closed_reason !== "Other" ? lead.closed_reason ?? "" : "";
    const reasonDetail = h("input", { type: "text", value: reasonText, "aria-label": "Reason detail", placeholder: "Detail (optional)", maxlength: 250 });
    // Kept as "Reason: detail" so Insights can count reasons.
    const reason = { get value() { return reasonSel.value ? `${reasonSel.value}${reasonDetail.value.trim() ? `: ${reasonDetail.value.trim()}` : ""}` : reasonDetail.value.trim(); } };
    const closing = (s: LeadStatus) => s === "not_a_fit" || s === "lost" || s === "no_response";
    const saveProgress = action("Save", async (_b, st) => {
      const s = stageSel.value as LeadStatus;
      const o = owner.value.trim().toLowerCase();
      if (o && !o.endsWith("@ryoma-insight.co.uk")) throw new Error("The owner must be a Ryoma address");
      if (s === "call_booked" && !callAt.value) throw new Error("Enter the call date and time");
      if ((s === "not_a_fit" || s === "lost") && !reason.value.trim()) throw new Error("Say why it closed");
      const callIso = callAt.value ? new Date(callAt.value).toISOString() : null;
      const contact = s !== lead.status && s !== "not_a_fit" && s !== "no_response" ? new Date().toISOString() : lead.last_contact_at;
      await db.updateLead(lead.id, {
        status: s, owner: o || null, call_at: callIso, next_step: nextStep.value.trim() || null, next_step_due: nextDue.value || null,
        closed_reason: closing(s) ? reason.value.trim() || (s === "no_response" ? `No reply after ${lead.touch_count} touches` : null) : null, last_contact_at: contact,
        next_touch_due: s === "prospect" || s === "contacted" ? lead.next_touch_due : null,
      });
      if (s !== lead.status) await log("status", `${STAGE_LABEL[lead.status]} → ${STAGE_LABEL[s]}${reason.value.trim() && closing(s) ? `: ${reason.value.trim()}` : ""}`);
      if (callIso && callIso !== lead.call_at) await log("call", `Discovery call booked for ${when(callIso)}.`);
      st.textContent = "Saved";
      await render();
    });

    // ---- PandaDoc: proposals and engagement letters
    const docs = docsApi(db);
    const signed = documents.some((d) => d.status === "completed");
    const year = String(now.getFullYear());
    const usedThisYear = allDocs.filter((d) => (d.created_at ?? d.sent_at ?? "").startsWith(year)).length;
    const recipients = [
      ...contacts.filter((c) => c.email && !c.do_not_contact).map((c) => ({ email: c.email!, name: c.name })),
      ...(lead.email && !contacts.some((c) => c.email === lead.email) ? [{ email: lead.email, name: lead.name }] : []),
    ];
    const dKind = h("select", { "aria-label": "Document" }, (["engagement_letter", "proposal"] as DocKind[]).map((k) => h("option", { value: k }, DOC_KIND_LABEL[k])));
    const dTemplate = h("select", { "aria-label": "PandaDoc template" }, h("option", { value: "" }, "Loading templates…"));
    const dTo = h("select", { "aria-label": "Send to" }, recipients.length ? recipients.map((r) => h("option", { value: r.email }, `${r.name} <${r.email}>`)) : h("option", { value: "" }, "Add a contact with an email first"));
    const dAudit = h("input", { type: "number", min: "0", step: "1", class: "input-short", "aria-label": "Audit fee (£)", value: t.band && (t.fit === "audit" || t.fit === "quote" || t.fit === "review") ? String(t.band.feePence / 100) : "" });
    const dImpl = h("input", { type: "number", min: "0", step: "1", class: "input-short", "aria-label": "Implementation fee (£)", placeholder: "After the audit" });
    const dRet = h("input", { type: "number", min: "0", step: "1", class: "input-short", "aria-label": "Retainer a month (£)", placeholder: "After the audit" });
    let templatesLoaded = false;
    const loadTemplates = async () => {
      if (templatesLoaded) return;
      templatesLoaded = true;
      try {
        const list = await docs.templates();
        mount(dTemplate, list.length ? list.map((x) => h("option", { value: x.id }, x.name)) : h("option", { value: "" }, "No templates in PandaDoc yet"));
      } catch (e) {
        mount(dTemplate, h("option", { value: "" }, e instanceof Error ? e.message : String(e)));
      }
    };
    const poundsIn = (el: HTMLInputElement) => (el.value.trim() === "" ? null : Math.round(Number(el.value) * 100));
    const sendDoc = action("Send for signature", async (_b, st) => {
      if (!dTemplate.value) throw new Error("Choose a PandaDoc template");
      const to = recipients.find((r) => r.email === dTo.value);
      if (!to) throw new Error("Choose who signs");
      if (usedThisYear >= FREE_PLAN_DOCS_PER_YEAR && !confirm(`PandaDoc's free plan allows ${FREE_PLAN_DOCS_PER_YEAR} documents a year and ${usedThisYear} have been sent. Try anyway?`)) return;
      const [first, ...rest] = to.name.trim().split(/\s+/);
      st.textContent = "Creating in PandaDoc…";
      await docs.send({
        lead_id: lead.id, template_id: dTemplate.value, template_name: dTemplate.selectedOptions[0]?.textContent ?? null, kind: dKind.value as DocKind,
        recipient: { email: to.email, first_name: first ?? to.name, last_name: rest.join(" ") }, company: lead.company ?? lead.name, orders_band: lead.orders_band,
        audit_fee_pence: poundsIn(dAudit), implementation_fee_pence: poundsIn(dImpl), retainer_pence: poundsIn(dRet), sender: sign ?? "Ryoma Insight",
      });
      await render();
    }, { primary: true });
    const docRows = documents.map((d) => [
      h("strong", null, d.name), d.recipient_email ?? "–",
      h("span", { class: `pill pill-doc-${d.status}` }, DOC_STATUS_LABEL[d.status]),
      d.sent_at ? date(d.sent_at) : "–", d.completed_at ? date(d.completed_at) : "–",
      h("span", { class: "row-actions tight" },
        FINAL_STATUSES.includes(d.status) ? "" : action("Check status", async () => { await docs.refresh(d.id!); await render(); }),
        d.status === "draft" ? action("Send", async () => { await docs.sendDraft(d.id!); await render(); }) : "",
        d.status === "completed" ? action("Download signed PDF", async () => {
          const blob = await docs.download(d.id!);
          const url = URL.createObjectURL(blob);
          const a = h("a", { href: url, download: `${d.name}.pdf` });
          document.body.append(a); a.click(); a.remove();
          setTimeout(() => URL.revokeObjectURL(url), 1000);
        }) : "",
        docs.demo ? "" : h("a", { class: "btn btn-quiet", href: `https://app.pandadoc.com/a/#/documents/${d.pandadoc_id}`, target: "_blank", rel: "noopener" }, "Open in PandaDoc")),
    ]);
    const showDocs = !lead.do_not_contact && (documents.length > 0 || ["replied", "call_booked", "call_done", "proposal_sent", "won"].includes(lead.status));
    const docsCard = showDocs
      ? h("div", { class: "card" }, h("h2", null, "Proposal and engagement letter"),
          documents.length ? table(["Document", "To", "Status", "Sent", "Signed", ""], docRows) : "",
          signed && !audit ? notice("ok", "Signed. Start the audit below.") : "",
          lead.status === "won" ? "" : h("details", { open: documents.length === 0 && lead.status === "call_done", onToggle: () => void loadTemplates() },
            h("summary", null, documents.length ? "Send another" : "Send from a PandaDoc template"),
            h("div", { class: "form-grid" },
              h("label", null, "Document"), dKind, h("label", null, "Template"), dTemplate, h("label", null, "Signs it"), dTo,
              h("label", null, "Audit fee £"), dAudit, h("label", null, "Implementation £"), dImpl, h("label", null, "Retainer a month £"), dRet,
              h("span"), sendDoc),
            h("p", { class: "muted small" }, `Fills the template's variables (company, contact, fees ex VAT, 5 working days, up to 6 weeks) and sends it from PandaDoc for e-signature. Status is checked every hour, or now with "Check status". ${usedThisYear} of ${FREE_PLAN_DOCS_PER_YEAR} documents used this year on the free plan.${docs.demo ? " Demo: nothing is sent." : ""}`)),
        )
      : "";

    // ---- Start the audit
    const clientName = h("input", { type: "text", value: lead.company ?? lead.name, "aria-label": "Client name", maxlength: 200 });
    const auditTitle = h("input", { type: "text", value: "Shipping and packaging audit", "aria-label": "Audit title", maxlength: 200 });
    const start = action("Make them a client and start the audit", async () => {
      if (!clientName.value.trim()) throw new Error("Enter the client name");
      const clients = await db.listClients();
      const existing = clients.find((c) => c.name.toLowerCase() === clientName.value.trim().toLowerCase());
      const client = existing ?? (await db.createClient(clientName.value));
      const a = await db.createAudit(client.id, auditTitle.value || "Shipping and packaging audit");
      if (t.band && t.fit === "audit") await db.updateAudit(a.id, { audit_fee_pence: t.band.feePence });
      await db.updateLead(lead.id, { status: "won", client_id: client.id, audit_id: a.id, last_contact_at: new Date().toISOString(), next_step: null, next_step_due: null, next_touch_due: null });
      await log("status", `${STAGE_LABEL[lead.status]} → ${STAGE_LABEL.won}: audit created for ${client.name}.`);
      celebrate("Client won");
      toast(`${client.name} is a client. Audit started.`);
      location.hash = `#/audit/${a.id}`;
    }, { primary: true });

    // ---- Notes
    const noteKind = h("select", { "aria-label": "Kind" }, h("option", { value: "note" }, "Note"), h("option", { value: "call" }, "Call"), h("option", { value: "email" }, "Email"));
    const noteText = h("textarea", { "aria-label": "Note", rows: 2, maxlength: 4000, placeholder: "What happened, what was agreed" });
    const noteDraft = keepDraft(`note-${lead.id}`, { kind: noteKind, text: noteText });
    const addNote = action("Add note", async () => {
      if (!noteText.value.trim()) throw new Error("Write a note first");
      await log(noteKind.value as LeadNote["kind"], noteText.value);
      if (noteKind.value !== "note") await db.updateLead(lead.id, { last_contact_at: new Date().toISOString() });
      noteDraft.clear();
      await render();
    });
    const del = action("Delete this lead", async () => {
      if (!confirm(`Delete ${lead.company ?? lead.name}, its contacts, touches and notes? Use this when someone asks us to erase their details. It cannot be undone. If they only asked not to be contacted, use "They asked not to be contacted" instead, so we never approach them again.`)) return;
      await db.deleteLead(lead.id);
      location.hash = "#/leads";
    });

    const noteWhen = (at: string | null | undefined) => `${date(at ?? null)} ${at ? new Date(at).toLocaleTimeString("en-GB", { hour: "2-digit", minute: "2-digit", timeZone: "Europe/London" }) : ""}`.trim();
    const deleteTouch = (x: Touch) => deleteButton(`touch ${x.touch_no} from ${date(x.at)}`,
      `Delete touch ${x.touch_no} (${CHANNEL_LABEL[x.channel].toLowerCase()}, ${date(x.at)})? The sequence count is worked out again from the touches left. The stage stays as it is.`,
      async () => {
        await db.deleteTouch(x.id!);
        await db.updateLead(lead.id, touchesLeftPatch(lead, touches.filter((y) => y.id !== x.id)));
        await render();
      });
    const touchTable = () => table(["#", "When", "Channel", "Outcome", "By", "Note", ""],
      touches.map((x) => [String(x.touch_no), date(x.at), CHANNEL_LABEL[x.channel], OUTCOME_LABEL[x.outcome], x.by_email?.split("@")[0] ?? "–", x.note ?? "", deleteTouch(x)]));

    const showSequence = isOpen(lead.status) && !lead.do_not_contact && (outbound || lead.status === "prospect" || lead.status === "contacted" || touches.length > 0);
    const showReply = !outbound && !lead.do_not_contact;

    mount(root,
      h("p", { class: "crumbs" }, h("a", { href: "#/leads" }, "Leads"), " / ", lead.company ?? lead.name),
      h("div", { class: "page-head" },
        h("h1", null, leadTitle(lead)), originPill(lead.origin), stagePill(lead.status), priorityPill(t.priority),
        lead.do_not_contact ? h("span", { class: "pill pill-dnc" }, "Do not contact") : "",
        next ? h("span", { class: `due ${next.overdue ? "late" : ""}` }, h("strong", null, next.what), next.due ? ` · ${next.overdue ? "was due " : "due "}${when(next.due, now)}` : "") : "",
      ),
      lead.status === "new" && next?.overdue ? notice("error", `The reply is late: we promise an answer within ${REPLY_WORKING_DAYS} working days.`) : "",
      lead.do_not_contact ? notice("warn", "They asked not to be contacted. This record is kept only so we never approach them again.") : "",
      h("div", { class: "lead-grid" },
        h("div", { class: "card" }, h("h2", null, fromWebsite ? "What they told us" : "About them"),
          h("dl", { class: "facts" }, told.flatMap(([k, v]) => [h("dt", null, k), h("dd", null, v)])),
          h("details", null, h("summary", { class: "small" }, "Edit details"),
            h("div", { class: "form-grid" },
              h("label", null, "Company"), eCompany, h("label", null, "Website"), eWebsite, h("label", null, "Sector"), eSector,
              h("label", null, "Orders a month"), eOrders, h("label", null, "Origin"), eOrigin, h("label", null, "Partner"), ePartner,
              h("label", null, "Source"), eSource, h("label", null, "Source detail"), eSourceDetail, h("label", null, "Tags"), eTags,
              h("label", null, "Legal form"), eLegal, h("span"), saveDetails)),
        ),
        h("div", { class: "card" }, h("h2", null, "Our read"),
          h("p", null, h("strong", null, "Offer: "), t.offer),
          (() => {
            // An outbound prospect has no reply deadline; their emails are on the contacts.
            const checks = outbound ? t.checks.filter((c) => !c.startsWith("No valid email")) : t.checks;
            return checks.length ? h("ul", { class: "checks" }, checks.map((c) => h("li", null, c))) : "";
          })(),
          h("p", { class: "muted small" }, "From the same bands as the website's fit check and pricing page. Confirm fit on the discovery call; never estimate their saving before we have their data."),
          ["replied", "call_booked", "call_done"].includes(lead.status)
            ? h("details", { open: lead.status === "call_booked" }, h("summary", null, "Discovery call questions"),
                CALL_QUESTIONS.flatMap((g) => [h("h3", null, g.topic), h("ul", { class: "checks" }, g.questions.map((q) => h("li", null, q)))]),
                h("p", { class: "muted small" }, "Note the answers in this lead's notes. They decide the cross-border and packaging rule checks in the audit."))
            : "",
        ),
      ),
      h("div", { class: "card" }, h("h2", null, `People (${contacts.length})`),
        contacts.length ? table(["Name", "Role", "Email", "LinkedIn", "", ""], contactRows) : notice("info", "No contacts yet. Outbound approaches work best to two or three people at the company."),
        lead.do_not_contact ? "" : h("div", { class: "inline-form" }, cName, cRole, cEmail, cLinked, addContact),
      ),
      showSequence
        ? h("div", { class: "card" }, h("h2", null, `Sequence: touch ${Math.min(lead.touch_count, SEQUENCE_LENGTH)} of ${SEQUENCE_LENGTH}`),
            h("ol", { class: "sequence" }, SEQUENCE.map((s) => {
              const done = touches.filter((x) => x.touch_no === s.no);
              return h("li", { class: done.length ? "done" : s.no === touchNo ? "next" : "" },
                h("strong", null, `Touch ${s.no}`), ` ${s.what}`,
                done.length ? h("span", { class: "muted small" }, ` · ${done.map((d) => `${date(d.at)}, ${OUTCOME_LABEL[d.outcome].toLowerCase()}`).join("; ")}`)
                  : s.no === touchNo && lead.next_touch_due ? h("span", { class: "muted small" }, ` · due ${when(lead.next_touch_due, now)}`) : "");
            })),
            touchNo <= SEQUENCE_LENGTH ? (draft.blocked ? [
              h("h3", null, `Touch ${touchNo}`),
              notice("warn", draft.body),
            ] : [
              h("h3", null, `Draft for touch ${touchNo}`),
              step?.channel === "email" && coldEmailRule(lead.legal_form).check ? notice("warn", coldEmailRule(lead.legal_form).why!) : "",
              h("div", { class: "reply" }, h("label", null, "Subject ", dSubject), dBody),
              h("div", { class: "row-actions" }, dMail),
            ]) : notice("info", `All ${SEQUENCE_LENGTH} touches sent. If there is no reply by ${when(lead.next_touch_due, now)}, set the stage to "No response".`),
            h("div", { class: "inline-form" }, h("label", null, "To ", tContact), tChannel, tOutcome, tWhen, tNote, logTouch, touchDraft.status),
            touches.length ? h("details", null, h("summary", { class: "small" }, `Touches logged (${touches.length})`), touchTable()) : "",
            h("p", { class: "muted small" }, "Log each touch when it goes out. The next one is scheduled in working days. A reply moves the lead to Replied; a request to stop closes it as do-not-contact."),
          )
        : touches.length
          ? h("div", { class: "card" }, h("h2", null, "Touches"), touchTable())
          : "",
      showReply
        ? h("div", { class: "card" }, h("h2", null, lead.replied_at ? "Reply (sent)" : "Reply"),
            lead.replied_at ? h("p", { class: "muted small" }, `Replied ${date(lead.replied_at)}. The draft below is kept for follow-ups.`) : h("p", { class: "muted small" }, `Send from business@ryoma-insight.co.uk. Fill the [square brackets] first.${lead.email ? "" : " There is no valid email for this lead."}`),
            h("div", { class: "reply" }, h("label", null, "Subject ", subject), body),
            h("div", { class: "row-actions" }, replyDraft.status, lead.email ? openMail : "", copy, markReplied),
          )
        : "",
      h("div", { class: "card" }, h("h2", null, "Progress"),
        h("div", { class: "form-grid" },
          h("label", null, "Stage"), stageSel,
          h("label", null, "Owner"), owner,
          h("label", null, "Discovery call"), callAt,
          h("label", null, "Next step"), h("div", null, nextStep, " ", nextDue),
          h("label", null, "If closed, why"), h("div", { class: "reason-row" }, reasonSel, reasonDetail),
          h("span"), saveProgress,
        ),
      ),
      docsCard,
      audit
        ? h("div", { class: "card combined" }, h("h2", null, "Client"), h("p", null, "Audit: ", h("a", { href: `#/audit/${audit.id}` }, `${audit.client_name}: ${audit.title}`)))
        : isOpen(lead.status) && !["new", "prospect", "contacted"].includes(lead.status)
          ? h("details", { class: "card", open: lead.status === "proposal_sent" || signed },
              h("summary", null, "Engagement letter signed? Start the audit"),
              h("p", { class: "muted small" }, `Creates the client and the audit${t.band && t.fit === "audit" ? `, with the audit fee set to £${(t.band.feePence / 100).toLocaleString("en-GB")} from their order band (change it on the audit if agreed otherwise)` : ""}. Their order data then comes in through the audit page.`),
              h("div", { class: "form-grid" }, h("label", null, "Client name"), clientName, h("label", null, "Audit"), auditTitle, h("span"), start),
            )
          : "",
      h("div", { class: "card" }, h("h2", null, "Notes and history"),
        notes.length ? h("ol", { class: "history" }, notes.map((n) => h("li", { class: `h-${n.kind}` },
          h("div", { class: "history-row" },
            h("span", { class: "muted small" }, `${noteWhen(n.at)} · ${n.by_email ?? "Platform"}`),
            n.id ? deleteButton(`the ${n.kind === "note" ? "note" : "entry"} from ${noteWhen(n.at)}`, "Delete this entry from the history? The activity log keeps a record that it was deleted, but not what it said.", async () => {
              await db.deleteLeadNote(n.id!);
              await render();
            }) : ""),
          h("div", null, n.body)))) : notice("info", "No notes yet."),
        h("div", { class: "inline-form" }, noteKind, noteText, addNote, noteDraft.status),
      ),
      h("p", { class: "muted small" },
        lead.do_not_contact ? "Kept as a do-not-contact record. "
          : lead.purge_after ? `Kept until ${date(lead.purge_after)} (2 years after our last contact, as our privacy notice says), unless they become a client. `
          : "Kept with the client record. ",
        lead.do_not_contact ? "" : optOutBtn, " ", del, " ", h("a", { class: "btn btn-quiet", href: `#/activity?record=${lead.id}` }, "History")),
    );
  };
  await render();
  return root;
}

